<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { Download, LoaderCircle } from 'lucide-vue-next';
import { confirmCancelNativeOta, nativeOtaDeepLinkState as update, requestCancelNativeOta } from '../native-runtime';

const dialog = ref<HTMLElement>();
const title = computed(() => update.confirmingCancel ? '¿Cancelar la actualización?'
  : update.phase === 'applying' ? 'Aplicando actualización…'
  : update.phase === 'error' ? 'No se pudo actualizar'
  : update.phase === 'complete' ? 'Aplicación actualizada' : 'Descargando actualización');
let previousFocus: HTMLElement | null = null;
let previousOverflow = '';
watch(() => update.open, async (open) => {
  if (!open) {
    document.body.style.overflow = previousOverflow;
    previousFocus?.focus();
    return;
  }
  previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  previousOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  await nextTick();
  dialog.value?.focus();
});
watch(() => update.confirmingCancel, async () => {
  await nextTick();
  dialog.value?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
});

function trapKeyboard(event: KeyboardEvent) {
  if (!update.open) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
  if (event.key !== 'Tab') return;
  const buttons = [...(dialog.value?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? [])];
  const first = buttons[0];
  const last = buttons[buttons.length - 1];
  if (!first) { event.preventDefault(); return; }
  if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.value)) {
    event.preventDefault(); last?.focus();
  } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog.value)) {
    event.preventDefault(); first.focus();
  }
}
function trapFocus(event: FocusEvent) {
  if (update.open && dialog.value && !dialog.value.contains(event.target as Node)) dialog.value.focus();
}
onMounted(() => {
  window.addEventListener('keydown', trapKeyboard, true);
  window.addEventListener('focusin', trapFocus, true);
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', trapKeyboard, true);
  window.removeEventListener('focusin', trapFocus, true);
  if (update.open) document.body.style.overflow = previousOverflow;
});
</script>

<template>
  <Teleport to="body">
    <div v-if="update.open" class="ota-backdrop">
      <section ref="dialog" class="ota-dialog" role="dialog" aria-modal="true" tabindex="-1" aria-labelledby="ota-title" aria-describedby="ota-message">
        <div class="ota-content">
          <LoaderCircle v-if="['resolving', 'downloading', 'applying'].includes(update.phase)" class="ota-loader" :size="36" aria-hidden="true" />
          <Download v-else :size="36" aria-hidden="true" />
          <h2 id="ota-title">{{ title }}</h2>
          <template v-if="update.confirmingCancel">
            <p id="ota-message">Si cancelas, esta actualización no se aplicará.</p>
            <div class="ota-actions">
              <button type="button" autofocus @click="confirmCancelNativeOta(false)">Continuar descarga</button>
              <button type="button" class="ota-danger" @click="confirmCancelNativeOta(true)">Sí, cancelar</button>
            </div>
          </template>
          <template v-else>
            <p class="ota-version">{{ update.version === 'latest' ? 'Buscando la última versión…' : `Versión ${update.version}` }}</p>
            <p id="ota-message" :role="update.phase === 'error' ? 'alert' : 'status'">{{ update.message || 'No abandones la aplicación, mantén esta ventana abierta para aplicar la descarga.' }}</p>
            <div v-if="['resolving', 'downloading', 'applying'].includes(update.phase)" class="ota-progress">
              <progress :value="update.phase === 'resolving' ? undefined : update.percent" max="100" aria-label="Progreso de descarga" />
              <span>{{ update.phase === 'resolving' ? 'Preparando descarga…' : `${update.percent}%` }}</span>
            </div>
            <button type="button" :disabled="update.phase === 'applying'" @click="requestCancelNativeOta">Cancelar</button>
          </template>
        </div>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.ota-backdrop{position:fixed;inset:0;z-index:3000;display:grid;place-items:center;padding:calc(20px + env(safe-area-inset-top,0px)) 20px calc(20px + env(safe-area-inset-bottom,0px));overflow:auto;background:#080c1999;backdrop-filter:blur(5px)}
.ota-dialog{width:min(420px,100%);padding:0;border:0;border-radius:22px;color:#182038;background:#fff;box-shadow:0 28px 90px #0509144d;outline:none}
.ota-content{padding:30px;display:grid;gap:18px;justify-items:center;text-align:center}
.ota-content>svg{color:#075cce}.ota-content h2{margin:0;font-size:23px;line-height:1.3}.ota-content p{margin:0;font-size:15px;line-height:1.6}.ota-version{color:#667085;font-weight:600}
.ota-progress{display:grid;gap:8px;width:100%;font-size:14px;font-variant-numeric:tabular-nums}.ota-progress progress{width:100%;height:12px;accent-color:#075cce}
.ota-content button{min-height:46px;width:100%;padding:10px 16px;border:1px solid #dfe2e7;border-radius:11px;color:#182038;background:#fff;font-size:15px;font-weight:650;cursor:pointer}.ota-content button:disabled{opacity:.5;cursor:wait}.ota-content button:focus-visible{outline:3px solid #075cce;outline-offset:3px}
.ota-actions{width:100%;display:grid;gap:10px}.ota-actions .ota-danger{background:#b42318;border-color:#b42318;color:#fff}
.ota-loader{animation:ota-spin 1s linear infinite}@keyframes ota-spin{to{transform:rotate(360deg)}}
@media(prefers-reduced-motion:reduce){.ota-loader{animation:none}}
</style>
